import React from 'react';
import classnames from 'classnames';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import InputBase, { InputBaseProps } from '@material-ui/core/InputBase';
import Paper, { PaperProps } from '@material-ui/core/Paper';

import Chip, { ChipProps } from 'components/v2/Chip';
import { useLiveRef } from 'ui-utils';
import useUniqueId from 'hooks/useUniqueId';

const useStyles = (theme: Theme) =>
  createStyles({
    container: {
      transition: 'all 400ms',
      border: `1px solid ${theme.custom.palette.colorSupportPowder}`,
      padding: theme.spacing.unit,
    },
    countainerFocused: {
      border: `1px solid ${theme.custom.palette.colorSupportMidnight}`,
    },
    chip: {
      margin: theme.spacing.unit / 2,
    },
  });

type MultiChipInputState = string[];

type MultiChipInputStateUpdater = (value: MultiChipInputState) => void;

type UseMultiChipInput = {
  initialState?: MultiChipInputState;
  controller?: [MultiChipInputState, MultiChipInputStateUpdater] | [];
  onChange?: MultiChipInputStateUpdater;
};

export interface MultiChipInputProps extends WithStyles<typeof useStyles> {
  value: MultiChipInputState | [];
  inputValue: string;
  onChange: (event: React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onDelete: (event: React.SyntheticEvent, eventInfo: { chip: string }) => void;
  onAdd: () => void;
  inputBaseProps?: InputBaseProps;
  paperProps?: PaperProps;
  chipProps?: ChipProps;
}

const MultiChipInput: React.FC<MultiChipInputProps> = ({
  classes,
  value,
  inputValue,
  onAdd,
  onChange,
  onDelete,
  inputBaseProps,
  paperProps,
  chipProps,
}) => {
  const [isFocused, setFocused] = React.useState(false);
  const uid = useUniqueId();
  return (
    <Paper
      onClick={() => {
        // should use refs when possible.
        // refs in material ui sucks.
        // can't do this.
        // inputRef.current.focus();
        const input = document.getElementById(uid);
        if (input) {
          input.focus();
          setFocused(true);
        }
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      elevation={0}
      className={classnames(classes.container, { [classes.countainerFocused]: isFocused })}
      {...paperProps}
    >
      {(value as string[]).map((chip) => (
        <Chip
          className={classes.chip}
          color="supportPowder"
          label={chip}
          onDelete={(event) => onDelete(event, { chip })}
          {...chipProps}
        />
      ))}
      <InputBase
        id={uid}
        value={inputValue}
        onChange={onChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            // prevent default so it won't
            // submit form on "Enter"
            event.preventDefault();
            onAdd();
          }
        }}
        {...inputBaseProps}
      />
    </Paper>
  );
};
/**
 * Helper hook for the MultiChipInput component that takes care of the boilerplate that would come with implementing the add, remove and change text logic.
 * NOTE: This hook it not necessary to use the component at all. It's just a bundle of boilerplate bussiness logic to help implementation.
 * @param {UseMultiChipInput} configs - The configs to make the component controlled of not.
 * @param {UseMultiChipInput['initialState']} configs.initialState - The initial state for the component if you want to not control the state.
 * @param {UseMultiChipInput['controller']} configs.controller - A pair of state and state updater so that you can control the state from outside.
 * @param {UseMultiChipInput['onChange']} configs.onChange - A callback that will listen to any changes to the components state add, remove, text changes.
 */
export function useMultiChipInput({ initialState = [], controller = [], onChange = () => {} }: UseMultiChipInput = {}) {
  const [chips, setChips] = React.useState(() => initialState);
  const [input, setInput] = React.useState('');
  const [state, stateUpdater] = controller;

  const cbRef = useLiveRef(onChange);

  const chipState = (stateUpdater ? state : chips) as MultiChipInputState;
  const updater = (stateUpdater || setChips) as MultiChipInputStateUpdater;

  const handleChange = React.useCallback((event) => {
    setInput(event.currentTarget.value);
  }, []);

  const handleRemove = React.useCallback(
    (_, { chip }) => {
      const newChips = [...chipState].filter((stateChip) => chip !== stateChip);
      cbRef.current(newChips);
      updater(newChips);
    },
    [updater, cbRef, chipState],
  );

  const handleAdd = React.useCallback(() => {
    const newChips = [...chipState, input];
    setInput('');
    cbRef.current(newChips);
    updater(newChips);
  }, [updater, cbRef, chipState, input]);

  const props = React.useMemo(
    () => ({
      value: chipState,
      inputValue: input,
      onChange: handleChange,
      onDelete: handleRemove,
      onAdd: handleAdd,
    }),
    [chipState, handleChange, handleRemove, handleAdd, input],
  );

  return {
    state: chipState,
    inputValue: input,
    handleChange,
    handleRemove,
    handleAdd,
    props,
  };
}

export default withStyles(useStyles)(MultiChipInput);
